import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { useToast } from "@/context/ToastContext";
import { userMigrationApi } from "@/lib/api";

interface UserMigrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onMigrationSuccess?: () => void;
}

interface UserConnectionResult {
    success: boolean;
    message: string;
    total_users?: number;
    main_users?: number;
    subusers?: number;
}

interface UserMigrationStats {
    total_v1_users: number;
    users_created: number;
    users_skipped: number;
    roles_assigned: number;
    subusers_skipped: number;
    errors: string[];
}

export const UserMigrationModal: React.FC<UserMigrationModalProps> = ({
    isOpen,
    onClose,
    onMigrationSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [testing, setTesting] = useState<boolean>(false);
    const [migrating, setMigrating] = useState<boolean>(false);
    const [connectionResult, setConnectionResult] =
        useState<UserConnectionResult | null>(null);
    const [stats, setStats] = useState<UserMigrationStats | null>(null);
    const statsEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (stats && statsEndRef.current) {
            statsEndRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end",
            });
        }
    }, [stats]);

    const activeCompanyId = useMemo<string | null>(() => {
        const stored = localStorage.getItem("vigepra_active_company");
        return stored && stored !== "main" ? stored : null;
    }, [isOpen]);

    const isMainContext = activeCompanyId === null;

    const handleTestConnection = async (): Promise<void> => {
        setTesting(true);
        setConnectionResult(null);

        try {
            const res = await userMigrationApi.testUsers();
            const connData =
                (
                    res as {
                        data?: {
                            success: boolean;
                            message: string;
                            total_users?: number;
                            main_users?: number;
                            subusers?: number;
                        };
                    }
                )?.data || res;
            if (connData) {
                setConnectionResult(connData);
                if (connData.success) {
                    showSuccess(t("tools.user_migration.connection_ok"));
                } else {
                    showError(
                        connData.message ||
                            t("tools.user_migration.connection_error")
                    );
                }
            }
        } catch (error: unknown) {
            console.error(error);
            const err = error as {
                response?: {
                    data?: { message?: string; data?: { message?: string } };
                };
                message?: string;
            };
            const errorMsg =
                err?.response?.data?.message ||
                err?.response?.data?.data?.message ||
                err?.message ||
                t("tools.user_migration.connection_error");
            showError(errorMsg);
            setConnectionResult({
                success: false,
                message: errorMsg,
            });
        } finally {
            setTesting(false);
        }
    };

    const handleRunMigration = async (): Promise<void> => {
        setMigrating(true);
        setStats(null);

        try {
            const res = await userMigrationApi.runUsers();
            if (res?.data) {
                setStats(res.data);
                showSuccess(t("tools.user_migration.migration_success"));
                onMigrationSuccess?.();
            }
        } catch (error: unknown) {
            console.error(error);
            const err = error as {
                response?: {
                    data?: {
                        message?: string;
                        errors?: Record<string, unknown>;
                    };
                };
                message?: string;
            };
            const errorMsg =
                err?.response?.data?.message ||
                (err?.response?.data?.errors
                    ? Object.values(err.response.data.errors).flat().join(" ")
                    : "") ||
                err?.message ||
                t("tools.user_migration.migration_error");

            showError(errorMsg);
            setStats({
                users_created: 0,
                users_skipped: 0,
                roles_assigned: 0,
                subusers_skipped: 0,
                total_users: 0,
                errors: [errorMsg],
            });
        } finally {
            setMigrating(false);
        }
    };

    const handleClose = (): void => {
        if (!migrating) {
            setConnectionResult(null);
            setStats(null);
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t("tools.user_migration.title")}
            maxWidth="xl"
            cancelLabel={t("tools.user_migration.close")}
            onSubmit={handleRunMigration}
            submitLabel={t("tools.user_migration.run_migration")}
            submittingLabel={t("tools.user_migration.migrating")}
            isSubmitting={migrating}
            submitDisabled={migrating || testing}
        >
            <div className="flex flex-col items-center justify-center p-2 space-y-6">
                <div className="flex flex-col items-center justify-center space-y-3 text-center">
                    <Icon
                        value="users"
                        size={32}
                        color="primary"
                        background
                        className="mb-2"
                    />
                    <p className="text-sm text-slate-600 dark:text-zinc-300 text-center">
                        {t("tools.user_migration.modal_subtitle")}
                    </p>
                </div>

                {/* Scope notice: shows if migrating all or just one company */}
                <div
                    className={`w-full rounded-xl border p-4 text-center flex flex-col items-center justify-center gap-1 ${
                        isMainContext
                            ? "border-amber-200 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20"
                            : "border-sky-200 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-950/20"
                    }`}
                >
                    <Icon
                        value={isMainContext ? "globe" : "building"}
                        size={18}
                        className={
                            isMainContext ? "text-amber-600" : "text-sky-600"
                        }
                    />
                    <p
                        className={`text-xs font-semibold mt-1 ${
                            isMainContext
                                ? "text-amber-900 dark:text-amber-200"
                                : "text-sky-900 dark:text-sky-200"
                        }`}
                    >
                        {isMainContext
                            ? t("tools.user_migration.scope_all_title")
                            : t("tools.user_migration.scope_company_title")}
                    </p>
                    <p
                        className={`text-xs leading-relaxed ${
                            isMainContext
                                ? "text-amber-700 dark:text-amber-300"
                                : "text-sky-700 dark:text-sky-300"
                        }`}
                    >
                        {isMainContext
                            ? t("tools.user_migration.scope_all_desc")
                            : t("tools.user_migration.scope_company_desc")}
                    </p>
                </div>

                {/* Information Card */}
                <div className="w-full rounded-xl border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900/50 dark:bg-blue-950/20 text-center flex flex-col items-center justify-center">
                    <Icon
                        value="checkCircle"
                        size={20}
                        className="text-blue-600 mb-1"
                    />
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 text-center">
                        {t("tools.user_migration.rules_notice_title")}
                    </h4>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1 leading-relaxed text-center">
                        {t("tools.user_migration.rules_notice_desc")}
                    </p>
                </div>

                {/* Connection Box Centered */}
                <div className="w-full flex flex-col items-center justify-center text-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-card">
                    <div>
                        <p className="text-sm font-semibold text-slate-800 dark:text-zinc-200">
                            {t("tools.user_migration.test_connection")}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {t("tools.tender_migration.ssh_config_hint")}
                        </p>
                    </div>

                    <Button
                        variant="secondary"
                        onClick={handleTestConnection}
                        disabled={testing || migrating}
                    >
                        <div className="flex items-center gap-2">
                            <Icon
                                value={testing ? "refresh" : "check"}
                                size={15}
                                className={testing ? "animate-spin" : ""}
                            />
                            <span>
                                {testing
                                    ? t(
                                          "tools.user_migration.testing_connection"
                                      )
                                    : t("tools.user_migration.test_connection")}
                            </span>
                        </div>
                    </Button>

                    {connectionResult && (
                        <div
                            className={`w-full p-4 rounded-xl border text-sm flex flex-col items-center justify-center text-center gap-2 ${
                                connectionResult.success
                                    ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800/40 dark:text-emerald-300"
                                    : "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/20 dark:border-rose-800/40 dark:text-rose-300"
                            }`}
                        >
                            <Icon
                                value={
                                    connectionResult.success
                                        ? "checkCircle"
                                        : "alertCircle"
                                }
                                size={22}
                                className="shrink-0"
                            />
                            <div className="w-full">
                                <p className="font-semibold text-center break-words">
                                    {connectionResult.message}
                                </p>
                                {connectionResult.main_users !== undefined && (
                                    <div className="text-xs mt-1 space-y-0.5 text-center font-medium">
                                        <p>
                                            {t(
                                                "tools.user_migration.main_users",
                                                {
                                                    count: connectionResult.main_users,
                                                }
                                            )}
                                        </p>
                                        <p className="opacity-80">
                                            {t(
                                                "tools.user_migration.subusers_detected",
                                                {
                                                    count:
                                                        connectionResult.subusers ??
                                                        0,
                                                }
                                            )}
                                        </p>
                                        <p className="opacity-70">
                                            {t(
                                                "tools.user_migration.total_users",
                                                {
                                                    count:
                                                        connectionResult.total_users ??
                                                        0,
                                                }
                                            )}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Migration Stats */}
                {stats && (
                    <div className="w-full space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-card text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                            <Icon
                                value="checkCircle"
                                size={16}
                                className="text-emerald-600"
                            />
                            <span className="font-semibold text-slate-900 dark:text-white">
                                {t("tools.user_migration.results_title")}
                            </span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tools.user_migration.users_created")}
                                </p>
                                <p className="text-lg font-bold mt-1 text-emerald-600">
                                    {stats.users_created}
                                </p>
                            </div>

                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tools.user_migration.users_skipped")}
                                </p>
                                <p className="text-lg font-bold mt-1 text-amber-600">
                                    {stats.users_skipped}
                                </p>
                            </div>

                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tools.user_migration.roles_assigned")}
                                </p>
                                <p className="text-lg font-bold mt-1 text-blue-600">
                                    {stats.roles_assigned}
                                </p>
                            </div>

                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tools.user_migration.subusers_skipped")}
                                </p>
                                <p className="text-lg font-bold mt-1 text-slate-800 dark:text-zinc-200">
                                    {stats.subusers_skipped}
                                </p>
                            </div>
                        </div>

                        {stats.errors?.length > 0 && (
                            <div className="mt-2 text-xs text-rose-600 space-y-1 text-center">
                                {stats.errors.map((err: string, i: number) => (
                                    <p key={i}>• {err}</p>
                                ))}
                            </div>
                        )}

                        <div ref={statsEndRef} />
                    </div>
                )}
            </div>
        </Modal>
    );
};
